import { useState, useEffect } from 'react'
import { X, Trash2, ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { searchHistory, type SearchHistoryEntry } from '../utils/searchHistory'
import { cn } from '@/lib/utils'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Component Props
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface SearchHistoryProps {
  onSelectHistory: (keywords: string[], params: { display: number; sort: string }) => void
  className?: string
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Helper Functions
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function formatTimestamp(timestamp: number): string {
  const now = Date.now()
  const diff = now - timestamp
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return '방금 전'
  if (minutes < 60) return `${minutes}분 전`
  if (hours < 24) return `${hours}시간 전`
  if (days < 7) return `${days}일 전`

  return new Date(timestamp).toLocaleDateString('ko-KR', {
    month: 'short',
    day: 'numeric',
  })
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Search History Component
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function SearchHistory({ onSelectHistory, className }: SearchHistoryProps) {
  const [history, setHistory] = useState<SearchHistoryEntry[]>([])
  const [isCollapsed, setIsCollapsed] = useState(false)

  // Load history on mount and when localStorage changes
  useEffect(() => {
    loadHistory()

    // Listen for storage events (cross-tab synchronization)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'keynews-search-history') {
        loadHistory()
      }
    }

    // Listen for custom event (same-tab real-time updates)
    const handleHistoryUpdate = () => {
      loadHistory()
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('search-history-updated', handleHistoryUpdate)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('search-history-updated', handleHistoryUpdate)
    }
  }, [])

  const loadHistory = () => {
    const entries = searchHistory.getRecent(10) // Max 10 items
    setHistory(entries)
  }

  const handleSelectHistory = (entry: SearchHistoryEntry) => {
    onSelectHistory(entry.keywords, entry.searchParams)
  }

  const handleRemoveEntry = (keywords: string[], e: React.MouseEvent) => {
    e.stopPropagation()
    searchHistory.remove(keywords)
    loadHistory()
  }

  const handleClearAll = () => {
    searchHistory.clearAll()
    loadHistory()
  }

  if (history.length === 0) {
    return null
  }

  return (
    <div className={cn('space-y-2', className)}>
      {/* Collapsible Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex min-h-11 w-full items-center justify-between text-base font-semibold transition-colors hover:text-primary lg:min-h-0"
        >
          <span>최근 검색</span>
          {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {/* Collapsible Content */}
      <div
        className={cn(
          'grid transition-all duration-200',
          isCollapsed ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'
        )}
      >
        <div className="overflow-hidden">
          <div className="space-y-2 pt-2">
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground">검색 기록이 없습니다.</p>
            ) : (
              <>
                <div className="space-y-1">
                  {history.map((entry) => (
                    <div
                      key={`${entry.keywords.join(',')}-${entry.timestamp}`}
                      className="group flex w-full items-start gap-2 rounded-md border bg-card p-2 transition-colors hover:bg-accent"
                    >
                      {/*
                        Siblings, not nested. The delete control used to live inside
                        the row's own <button>, which is invalid HTML and made screen
                        readers fold "Remove from history" into the row's name.
                      */}
                      <button
                        type="button"
                        onClick={() => handleSelectHistory(entry)}
                        className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {/* Keywords */}
                        <div className="mb-1 flex flex-wrap gap-1">
                          {entry.keywords.map((keyword) => (
                            <span
                              key={keyword}
                              className="inline-flex items-center rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                            >
                              {keyword}
                            </span>
                          ))}
                        </div>

                        {/* Metadata */}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{formatTimestamp(entry.timestamp)}</span>
                          <span>•</span>
                          <span>
                            {entry.searchParams.display}개 •{' '}
                            {entry.searchParams.sort === 'sim' ? '관련도순' : '최신순'}
                          </span>
                        </div>
                      </button>

                      {/* Remove Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        // Revealed on hover only, which never fires on touch. Always
                        // visible below lg, and on focus so keyboards reach it too.
                        className="h-6 w-6 shrink-0 p-0 opacity-100 transition-opacity focus-visible:opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                        onClick={(e) => handleRemoveEntry(entry.keywords, e)}
                      >
                        <X className="h-3 w-3" />
                        <span className="sr-only">검색 기록에서 삭제</span>
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Clear All Button */}
                <div className="flex items-center justify-between border-t pt-2">
                  <span className="text-xs text-muted-foreground">최대 10개까지 표시됩니다.</span>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
                        <Trash2 className="mr-1 h-3 w-3" />
                        모두 삭제
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>검색 기록 삭제</AlertDialogTitle>
                        <AlertDialogDescription>
                          모든 검색 기록을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>취소</AlertDialogCancel>
                        <AlertDialogAction onClick={handleClearAll}>삭제</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
